import { gql } from '@apollo/client';
import useLocalStorage from '../hooks/useLocalStorage';
import { getTokenFromCookies, getCookie } from '../hooks/useCookie';
// eslint-disable-next-line import/no-cycle
import { formatAddress } from '../components/eir-broadband/Broadband.util';

const localStore = useLocalStorage();
const ENDPOINT = {
  GET_ADDRESSLIST: gql`
    query getAddress($eirCode: String!) {
      addressList(eirCode: $eirCode) {
        addresses {
          addressLine1
          addressLine2
          addressLine3
          town
          county
          eircode
        }
      }
    }
  `,
  GET_AREACODES: gql`
    query getAreacodes {
      areaCodes {
        AREA_CODE
      }
    }
  `,
  GET_PREQUAL_RESULT: gql`
    mutation prequal(
      $reference: String
      $address: PrequalAddress!
      $offerType: String!
      $acquisitionType: String
      $phone: PrequalPhoneNumber
    ) {
      prequal(
        reference: $reference
        address: $address
        offerType: $offerType
        acquisitionType: $acquisitionType
        phone: $phone
      ) {
        reference
        prequalId
        onlinePrequalId
        ardId
        validationType
        services {
          broadbandTechnology
          maxSpeed
          networkTechnology
          serviceDate
          slotAvailable
        }
        billingSource
        customerType
        addressResponse {
          addressLine1
          addressLine2
          addressLine3
          county
          eirCode
          town
        }
        error
        phoneNumber
      }
    }
  `,
  GET_CO_PREQUAL_DATA: gql`
    query getCOPrequalDetailsByID($changeOfferReference: String!, $prequalId: Int!) {
      getCOPrequalDetailsByID(changeOfferReference: $changeOfferReference, prequalId: $prequalId) {
        reference
        prequalId
        onlinePrequalId
        ardId
        services {
          broadbandTechnology
          maxSpeed
          networkTechnology
          serviceDate
        }
        billingSource
        customerType
        addressResponse {
          addressLine1
          addressLine2
          addressLine3
          county
          eirCode
          town
        }
        error
      }
    }
  `,
  GET_PREQUAL_RESULTS: gql`
    query getPrequalResult($prequalId: Int!) {
      getPrequalResult(prequalId: $prequalId) {
        reference
        prequalId
        onlinePrequalId
        ardId
        services {
          broadbandTechnology
          maxSpeed
          networkTechnology
          serviceDate
        }
        billingSource
        customerType
        addressResponse {
          addressLine1
          addressLine2
          addressLine3
          county
          eirCode
          town
        }
        error
      }
    }
  `,
};

/**
 * @name getAddressList - Function for getting address list from eircode
 * @param {Object} graphql
 * @param {string} eircode
 * @returns {Array} - Returns list of address
 */
const getAddressList = async (graphql, eircode) => {
  try {
    const eirCode = eircode.toString();
    const res = await graphql.query({
      query: ENDPOINT.GET_ADDRESSLIST,
      variables: { eirCode },
    });
    if (res.data.addressList === null) {
      throw new Error('No address found for given eircode');
    }
    return res.data;
  } catch (error) {
    throw new Error(error);
  }
};

/**
 * @name getPrequalResult - Function for fetching prequal result based on address provided
 * @param {Object} graphql
 * @param {Object} address  - Formatted Address Object
 * @returns {Object} - Prequaled Data
 */
const getPrequalResult = async (
  graphql,
  address,
  phoneNumber = undefined
  // acquisitionType = 'NEW_ACQUISITION'
) => {
  const reference = localStore.getLocalStorage('PROSPECTUUID')
    ? localStore.getLocalStorage('PROSPECTUUID')
    : '';
  const offerType = 'POSTPAY';
  const token = getTokenFromCookies();
  const phone = phoneNumber;
  let variables = {};
  if (phoneNumber) {
    variables = {
      reference,
      address,
      offerType,
      token,
      phone,
      // acquisitionType,
    };
  } else {
    variables = {
      reference,
      address,
      offerType,
      token,
      // acquisitionType,
    };
  }

  try {
    const res = await graphql.mutate({
      mutation: ENDPOINT.GET_PREQUAL_RESULT,
      variables,
    });
    if (res.data === null) {
      throw new Error('No address found for given eircode');
    }
    return { data: res.data };
  } catch (error) {
    if (error?.graphQLErrors[0]?.extensions?.onlinePrequalId) {
      const result = error.graphQLErrors[0];
      return { result };
    }
    if (error?.graphQLErrors[0]?.extensions?.code === 'prequal-data-mismatch') {
      const result = error.graphQLErrors[0];
      return { result };
    }
    const result = error;
    console.log('error', error);
    return { result };
    // throw new Error(error);
  }
};

const getCOPrequalDetailsByID = async (graphql, prequalId = null) => {
  const token = getTokenFromCookies();
  const reference = getCookie('reference');
  let prequalID = '';
  if (!prequalId) {
    prequalID = localStore.getLocalStorage('prequalid');
  } else {
    prequalID = prequalId;
  }

  if (reference) {
    try {
      const { data } = await graphql.query({
        query: ENDPOINT.GET_CO_PREQUAL_DATA,
        variables: { token, changeOfferReference: reference, prequalId: prequalID },
      });
      let res = data;
      const formattedAddress = formatAddress(res.getCOPrequalDetailsByID.addressResponse);
      res = {
        ...res,
        getCOPrequalDetailsByID: { ...res.getCOPrequalDetailsByID, fullAddress: formattedAddress },
      };
      localStorage.setItem('CO_PREQUAL_RESULT', JSON.stringify(res.getCOPrequalDetailsByID));
      return { data };
    } catch (err) {
      throw new Error(err);
    }
  }
};
const getPrequalResults = async ({ graphql, prequalID }) => {
  try {
    const res = await graphql.query({
      query: ENDPOINT.GET_PREQUAL_RESULTS,
      variables: { prequalId: prequalID },
    });
    if (res.data === null) {
      throw new Error('No data found for given prequalId');
    }

    return { data: res.data };
  } catch (err) {
    throw new Error(err);
  }
};

const getAreaCodes = async graphql => {
  try {
    const res = await graphql.query({
      query: ENDPOINT.GET_AREACODES,
    });
    if (res.data === null) {
      throw new Error('No address found for given eircode');
    }
    return { data: res.data };
  } catch (err) {
    throw new Error(err);
  }
};

export {
  getAddressList,
  getPrequalResult,
  getAreaCodes,
  getCOPrequalDetailsByID,
  getPrequalResults,
};
